// Shared ride validation + normalisation. Used by adapters that ingest
// externally supplied ride JSON (fileImport, customUrl).
// Returns { valid, skipped } where `skipped` is an array of
// `{ ride, reason }` records — callers decide whether/how to surface them.
// Pure — no IO, no DOM.

// Accepts `YYYY-MM-DD` or ISO 8601 with an optional time component so two
// rides on the same day can be distinguished by start time.
const DATE_RE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

// The regex only checks shape, so `2024-99-99` slips through. Verify the
// calendar portion actually exists (no month/day overflow) and that the full
// string parses, otherwise it becomes an Invalid Date that throws downstream.
function isRealDate(value) {
    const [y, m, d] = value.slice(0, 10).split('-').map(Number);
    const cal = new Date(Date.UTC(y, m - 1, d));
    const calendarValid =
        cal.getUTCFullYear() === y && cal.getUTCMonth() + 1 === m && cal.getUTCDate() === d;
    return calendarValid && !Number.isNaN(new Date(value).getTime());
}

export function normaliseRides(tours, { defaultSource }) {
    const valid = [];
    const skipped = [];

    for (const ride of tours) {
        if (!ride.date) {
            skipped.push({ ride, reason: 'missing-date' });
            continue;
        }
        if (!DATE_RE.test(ride.date)) {
            skipped.push({ ride, reason: 'invalid-date-format' });
            continue;
        }
        if (!isRealDate(ride.date)) {
            skipped.push({ ride, reason: 'invalid-date' });
            continue;
        }
        if (ride.distance_km == null && ride.distance_m == null) {
            skipped.push({ ride, reason: 'missing-distance' });
            continue;
        }

        const normalised = { ...ride };
        if (normalised.distance_km == null) {
            normalised.distance_km = parseFloat((normalised.distance_m / 1000).toFixed(2));
            delete normalised.distance_m;
        }
        if (normalised.speed_kmh == null && normalised.duration_seconds > 0) {
            normalised.speed_kmh = parseFloat(
                ((normalised.distance_km / normalised.duration_seconds) * 3600).toFixed(2)
            );
        }
        if (!normalised.id) {
            normalised.id = btoa(`${normalised.date}-${parseFloat(normalised.distance_km).toFixed(2)}`);
        } else {
            normalised.id = String(normalised.id);
        }
        if (!normalised.source) {
            normalised.source = defaultSource;
        }
        valid.push(normalised);
    }

    return { valid, skipped };
}
